import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import {
  fetchOpenInvoicesReport,
  formatDateShortDe,
  formatDateTimeDe,
  formatEuroCompact,
  formatEuroDe,
  OPEN_INVOICE_BUCKET_LABELS,
  OPEN_INVOICE_BUCKETS,
  type OpenInvoiceBucket,
  type OpenInvoiceCustomer,
  type OpenInvoiceItem,
  type OpenInvoicesReport as OpenInvoicesReportData,
} from "@/lib/heroDocumentReports";
import "./auswertungen.css";

type DetailView = "invoices" | "customers";

function AgingBar({ report }: { report: OpenInvoicesReportData }) {
  const total = report.summary.open_amount;
  if (total <= 0) {
    return <p className="text-sm text-label-secondary">Keine offenen Rechnungen.</p>;
  }
  return (
    <div className="space-y-3">
      <div
        className="flex h-8 w-full overflow-hidden rounded-lg ring-1 ring-separator/40"
        role="img"
        aria-label="Offene Beträge nach Fälligkeit"
      >
        {OPEN_INVOICE_BUCKETS.map((bucket) => {
          const amount = report.summary.buckets[bucket]?.amount ?? 0;
          if (amount <= 0) return null;
          return (
            <div
              key={bucket}
              className={clsx(
                "eh-docreport-aging__segment",
                `eh-docreport-aging__segment--${bucket}`,
              )}
              style={{ width: `${(amount / total) * 100}%` }}
              title={`${OPEN_INVOICE_BUCKET_LABELS[bucket]}: ${formatEuroDe(amount)}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {OPEN_INVOICE_BUCKETS.map((bucket) => (
          <div key={bucket} className="flex items-center gap-2 text-xs text-label-secondary">
            <span
              className={clsx(
                "inline-block h-2.5 w-2.5 rounded-sm",
                "eh-docreport-aging__segment",
                `eh-docreport-aging__segment--${bucket}`,
              )}
              aria-hidden
            />
            <span>{OPEN_INVOICE_BUCKET_LABELS[bucket]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function overdueClass(bucket: OpenInvoiceBucket): string {
  if (bucket === "d61_plus") return "eh-docreport-overdue--severe";
  if (bucket === "d31_60") return "eh-docreport-overdue--high";
  if (bucket === "d1_30") return "eh-docreport-overdue--mild";
  return "";
}

const CUSTOMER_COLUMNS: Column<OpenInvoiceCustomer>[] = [
  { key: "kunde", header: "Kunde", render: (row) => row.kunde },
  {
    key: "open_count",
    header: "Offene Rechnungen",
    render: (row) => <span className="tabular-nums">{row.open_count}</span>,
  },
  {
    key: "open_amount",
    header: "Offener Betrag",
    render: (row) => (
      <span className="eh-docreport-amount--open tabular-nums font-medium">
        {formatEuroDe(row.open_amount)}
      </span>
    ),
  },
  {
    key: "max_days_overdue",
    header: "Max. überfällig",
    render: (row) =>
      row.max_days_overdue > 0 ? (
        <span className="tabular-nums">{row.max_days_overdue} Tage</span>
      ) : (
        <span className="text-label-tertiary">–</span>
      ),
  },
];

export function OpenInvoicesReport() {
  const [report, setReport] = useState<OpenInvoicesReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [detailView, setDetailView] = useState<DetailView>("invoices");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOpenInvoicesReport();
      setReport(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Offene Posten konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = report?.items ?? [];
    if (term) {
      rows = rows.filter((row) =>
        [row.document_number, row.kunde, row.auftragsnummer, row.beschreibung]
          .map((value) => String(value || "").toLowerCase())
          .join(" ")
          .includes(term),
      );
    }
    return rows;
  }, [report?.items, search]);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = report?.by_customer ?? [];
    if (term) {
      rows = rows.filter((row) => row.kunde.toLowerCase().includes(term));
    }
    return rows;
  }, [report?.by_customer, search]);

  const itemColumns = useMemo<Column<OpenInvoiceItem>[]>(
    () => [
      {
        key: "document_number",
        header: "Rechnung",
        render: (row) => (
          <div>
            <span className="font-medium text-label">{row.document_number || "–"}</span>
            <div className="text-xs text-label-secondary">{row.status_name || ""}</div>
          </div>
        ),
      },
      {
        key: "kunde",
        header: "Kunde / Projekt",
        render: (row) => (
          <div>
            <div>{row.kunde}</div>
            {row.auftrag_id ? (
              <ProjectOverviewLink
                auftragId={row.auftrag_id}
                label={[row.auftragsnummer, row.beschreibung].filter(Boolean).join(" – ")}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: "invoice_date",
        header: "Belegdatum",
        render: (row) => <span className="tabular-nums">{formatDateShortDe(row.invoice_date)}</span>,
      },
      {
        key: "due_date",
        header: "Fällig am",
        render: (row) => <span className="tabular-nums">{formatDateShortDe(row.due_date)}</span>,
      },
      {
        key: "days_overdue",
        header: "Überfällig",
        render: (row) =>
          row.days_overdue != null && row.days_overdue > 0 ? (
            <span className={clsx("tabular-nums font-medium", overdueClass(row.bucket))}>
              {row.days_overdue} Tage
            </span>
          ) : (
            <span className="text-label-tertiary">–</span>
          ),
      },
      {
        key: "total_amount",
        header: "Rechnungsbetrag",
        render: (row) => <span className="tabular-nums">{formatEuroDe(row.total_amount)}</span>,
      },
      {
        key: "open_amount",
        header: "Offen",
        render: (row) => (
          <span className="eh-docreport-amount--open tabular-nums font-semibold">
            {formatEuroDe(row.open_amount)}
          </span>
        ),
      },
    ],
    [],
  );

  const summary = report?.summary;

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" title="Offene Posten konnten nicht geladen werden">
          {error}
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TextInput
            label="Suche"
            placeholder="Rechnung, Kunde oder Projekt …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
              Neu laden
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-label-secondary">
          Offene Ausgangsrechnungen mit Restsaldo – App-Rechnungen (RE26-xxxx) und Hero-Belege,
          gleiche Datenbasis wie Buchhaltung → Offene Posten (Stand: {formatDateShortDe(report?.as_of)}).
          {report?.last_synced_at
            ? ` Letzter Hero-Sync: ${formatDateTimeDe(report.last_synced_at)}.`
            : ""}
        </p>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Offene Posten werden geladen …</span>
        </div>
      ) : report ? (
        <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Offen gesamt</p>
              <p className="eh-docreport-amount--open mt-1 text-2xl font-semibold tabular-nums">
                {formatEuroCompact(summary?.open_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.open_count ?? 0} Rechnungen
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Davon überfällig</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                {formatEuroCompact(summary?.overdue_amount)}
              </p>
              <p className="mt-1 text-[11px] text-label-tertiary">
                {summary?.overdue_count ?? 0} Rechnungen
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 lg:col-span-2">
              <p className="mb-2 text-xs text-label-secondary">Altersstruktur</p>
              <AgingBar report={report} />
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {OPEN_INVOICE_BUCKETS.map((bucket) => {
              const entry = report.summary.buckets[bucket] ?? { count: 0, amount: 0 };
              return (
                <div
                  key={bucket}
                  className={clsx(
                    "rounded-ios-lg px-4 py-3 shadow-ios-md ring-1 ring-separator/35",
                    "eh-docreport-aging-card",
                    `eh-docreport-aging-card--${bucket}`,
                  )}
                >
                  <p className="text-xs font-medium text-label-secondary">
                    {OPEN_INVOICE_BUCKET_LABELS[bucket]}
                  </p>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                    {formatEuroCompact(entry.amount)}
                  </p>
                  <p className="mt-1 text-[11px] text-label-tertiary">{entry.count} Rechnungen</p>
                </div>
              );
            })}
          </section>

          <section className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-separator p-0.5">
              <button
                type="button"
                className={clsx(
                  "rounded-md px-3 py-1.5 text-sm transition",
                  detailView === "invoices"
                    ? "bg-accent text-white"
                    : "text-label-secondary hover:text-label",
                )}
                onClick={() => setDetailView("invoices")}
              >
                Nach Rechnung
              </button>
              <button
                type="button"
                className={clsx(
                  "rounded-md px-3 py-1.5 text-sm transition",
                  detailView === "customers"
                    ? "bg-accent text-white"
                    : "text-label-secondary hover:text-label",
                )}
                onClick={() => setDetailView("customers")}
              >
                Nach Kunde
              </button>
            </div>
            <span className="text-sm text-label-secondary">
              {detailView === "invoices"
                ? `${filteredItems.length} Rechnungen`
                : `${filteredCustomers.length} Kunden`}
            </span>
          </section>

          {detailView === "invoices" ? (
            <Table
              columns={itemColumns}
              rows={filteredItems}
              rowKey={(row) => row.id}
              emptyText="Keine offenen Rechnungen gefunden."
              rowClassName={(row) =>
                row.bucket === "d61_plus" ? "eh-docreport-row--severe" : ""
              }
            />
          ) : (
            <Table
              columns={CUSTOMER_COLUMNS}
              rows={filteredCustomers}
              rowKey={(row) => `${row.kunden_id ?? row.kunde}`}
              emptyText="Keine offenen Rechnungen gefunden."
            />
          )}
        </>
      ) : null}
    </div>
  );
}
